/**
 * AlertSummaryPanel — Settings → Alerts & Reports → Alert summary (superadmin, T-226).
 *
 * The daily and weekly summary email: on/off and times (each person's own time
 * zone), whether non-urgent alert emails are being held for it right now (the
 * server decides; the card says why not), the latest sends, and a sample to
 * yourself. Sending is switched on separately, by the "Alert summary" email
 * route below; that switch is the go-live.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  getAlertDigestSettings,
  saveAlertDigestSchedule,
  sendSampleAlertDigest,
  type DigestSchedule,
  type DigestSettings,
} from '@/lib/alertDigestApi';
import { sendRowWords, summaryStatus } from '@/lib/alertDigestWords';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SHOWN_SENDS = 6;

const sameSchedule = (a: DigestSchedule, b: DigestSchedule) => JSON.stringify(a) === JSON.stringify(b);
/** What the server accepts (validateSchedule): HH:MM, 24-hour. */
const VALID_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

function periodDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * @param refreshKey bump it when something else on the page changed (the email switch
 *   below, the recipients above) so the status line is read again.
 */
export function AlertSummaryPanel({ refreshKey = 0 }: { refreshKey?: number } = {}) {
  const [settings, setSettings] = useState<DigestSettings | null>(null);
  const [draft, setDraft] = useState<DigestSchedule | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingSample, setSendingSample] = useState(false);
  const settingsRef = useRef<DigestSettings | null>(null);
  const draftRef = useRef<DigestSchedule | null>(null);
  settingsRef.current = settings;
  draftRef.current = draft;

  // Reads the settings; unsaved edits to the schedule are kept. Once the card has
  // shown settings, a failed re-read keeps them rather than hiding the card.
  const read = useCallback(async () => {
    try {
      const s = await getAlertDigestSettings();
      const before = settingsRef.current;
      const d = draftRef.current;
      const dirty = !!(before && d && !sameSchedule(before.schedule, d));
      setSettings(s);
      if (!dirty) setDraft(s.schedule);
      setLoadFailed(false);
    } catch {
      if (!settingsRef.current) setLoadFailed(true);
    }
  }, []);

  useEffect(() => { void read(); }, [read, refreshKey]);
  useEffect(() => {
    const onFocus = () => { void read(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [read]);

  const save = async () => {
    if (!draft) return;
    const submitted = draft;
    setSaving(true);
    try {
      const r = await saveAlertDigestSchedule(submitted);
      // An edit made while the save was running stays (review on #1139): the draft
      // takes the saved schedule only if it is still what was sent. The refs move now,
      // so the re-read below sees the saved schedule and the newer edit as unsaved.
      const next = settingsRef.current ? { ...settingsRef.current, schedule: r.schedule } : null;
      const current = draftRef.current;
      const nextDraft = current && !sameSchedule(current, submitted) ? current : r.schedule;
      settingsRef.current = next;
      draftRef.current = nextDraft;
      if (next) setSettings(next);
      setDraft(nextDraft);
      toast.success('Alert summary schedule saved');
      void read();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the schedule');
    } finally {
      setSaving(false);
    }
  };

  const sendSample = async () => {
    setSendingSample(true);
    try {
      const r = await sendSampleAlertDigest();
      toast.success(`Sample alert summary sent to ${r.to}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send the sample summary');
    } finally {
      setSendingSample(false);
    }
  };

  const setDaily = (patch: Partial<DigestSchedule['daily']>) =>
    setDraft((d) => (d ? { ...d, daily: { ...d.daily, ...patch } } : d));
  const setWeekly = (patch: Partial<DigestSchedule['weekly']>) =>
    setDraft((d) => (d ? { ...d, weekly: { ...d.weekly, ...patch } } : d));

  const status = settings ? summaryStatus(settings) : null;
  const dirty = !!(settings && draft && !sameSchedule(settings.schedule, draft));
  const timesValid = !!(draft && VALID_TIME.test(draft.daily.time) && VALID_TIME.test(draft.weekly.time));

  return (
    <div className="mt-6 rounded-lg border border-border bg-card p-5">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-[#8FB560]" />
        <h3 className="text-base font-semibold text-foreground">Alert summary</h3>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        One email per person on the alert list with the alerts since their last summary, sent at the time
        below in their own time zone (set on their Profile; Pacific if not set). Days with no alerts are skipped.
      </p>

      {loadFailed ? (
        <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground">
          <span>Could not load the alert summary settings.</span>
          <Button variant="outline" size="sm" onClick={() => void read()}>Try again</Button>
        </div>
      ) : !settings || !draft || !status ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="mt-4 space-y-6">
          <div className="flex items-start gap-2 rounded-md border border-border px-3 py-2.5 text-sm text-foreground">
            <span
              aria-hidden
              className={cn(
                'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                status.tone === 'on' ? 'bg-[#8FB560]' : status.tone === 'warn' ? 'bg-amber-500' : 'bg-muted-foreground/50',
              )}
            />
            <span>{status.text}</span>
          </div>

          <div>
            <div className="divide-y divide-border rounded-md border border-border">
              <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <Switch
                  checked={draft.daily.enabled}
                  onCheckedChange={(v) => setDaily({ enabled: v })}
                  aria-label="Daily summary"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-foreground">Daily</div>
                  <div className="text-xs text-muted-foreground">Every day, with the alerts since the last one</div>
                </div>
                <Input
                  type="time"
                  aria-label="Daily time"
                  className="w-32 tabular-nums"
                  value={draft.daily.time}
                  onChange={(e) => setDaily({ time: e.target.value })}
                />
              </div>
              <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <Switch
                  checked={draft.weekly.enabled}
                  onCheckedChange={(v) => setWeekly({ enabled: v })}
                  aria-label="Weekly summary"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-foreground">Weekly</div>
                  <div className="text-xs text-muted-foreground">Once a week, with the week's alerts</div>
                </div>
                <Select value={String(draft.weekly.day)} onValueChange={(v) => setWeekly({ day: Number(v) })}>
                  <SelectTrigger aria-label="Weekly day" className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((label, i) => <SelectItem key={label} value={String(i)}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  type="time"
                  aria-label="Weekly time"
                  className="w-32 tabular-nums"
                  value={draft.weekly.time}
                  onChange={(e) => setWeekly({ time: e.target.value })}
                />
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Button className="bg-[#8FB560] hover:bg-[#7a9d51]" disabled={!dirty || !timesValid || saving} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save schedule'}
              </Button>
              {!timesValid && <span className="text-xs text-muted-foreground">Pick a time for each summary.</span>}
            </div>
          </div>

          <div>
            <div className="mb-2 text-sm font-medium text-foreground">Latest summaries</div>
            {settings.recent.length === 0 ? (
              <p className="text-sm text-muted-foreground">No summaries sent yet.</p>
            ) : (
              <div className="divide-y divide-border rounded-md border border-border">
                {settings.recent.slice(0, SHOWN_SENDS).map((row) => {
                  const words = sendRowWords(row);
                  return (
                    <div key={`${row.cadence}|${row.recipient}|${row.period_end}`} className="flex items-center gap-3 px-3 py-2 text-sm">
                      <span className="w-14 shrink-0 text-muted-foreground">{row.cadence === 'daily' ? 'Daily' : 'Weekly'}</span>
                      <span className="w-24 shrink-0 tabular-nums text-muted-foreground">{periodDate(row.period_end)}</span>
                      <span className="min-w-0 flex-1 truncate text-foreground">{row.recipient}</span>
                      <span
                        className={cn(
                          'shrink-0 text-xs',
                          words.tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : words.tone === 'muted' ? 'text-muted-foreground' : 'text-foreground',
                        )}
                      >
                        {words.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-border pt-4">
            <p className="mb-2 text-xs text-muted-foreground">
              Email yourself a sample summary built from the last 7 days of real alerts. It goes only to you.
            </p>
            <Button variant="outline" disabled={sendingSample} onClick={() => void sendSample()}>
              {sendingSample ? 'Sending sample…' : 'Send me a sample summary'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
